import React, { FC } from 'react';
import { Box, Separator, SeparatorType, Heading } from '@bechtel/gds';
import { HistoryItem } from '@bechtel/client-common';
import { map, filter, flatMap, find, lowerCase } from 'lodash';
import { SummaryContainerStyled, SummaryContentStyled, DocumentPreviewContainerStyled } from './styled';
import { SummaryItem } from './item';
import { WeldLocation } from './location';
import { IsoContent } from './iso-content';
import { t, Trans } from '@lingui/macro';
import { Weld, Block, BlockStatusCode, CommodityAssignee } from '@bechtel/types';
import { CommoditySectionDetail } from '../types';

export interface WeldSummaryProps {
  weld: Weld;
}

export const WeldSummary: FC<WeldSummaryProps> = ({ weld }) => {
  const {
    isoDocument,
    workPackage,
    weldedWithProcedure,
    pipeDiameter,
    pipeMaterial,
    locationDescription,
    location,
    milestonesActivityHistory,
    milestones,
    sections,
    applicableWeldProcedures,
  } = weld;
  const commodityAssignee: CommodityAssignee[] | undefined = weld?.commodityAssignees ?? [];
  const blocks: Block[] = flatMap(milestones, 'blocks');
  const openBlocksCount = filter(blocks, (block) => block.currentStatusI18n === BlockStatusCode.BLOCK_OPENED).length;
  const milestonesActivityHistoryItems = milestonesActivityHistory ? [...milestonesActivityHistory.items] : [];
  const sortedMilestonesActivityHistory = milestonesActivityHistoryItems
    ? [...milestonesActivityHistoryItems]?.sort(
        (a, b) => new Date(b?.dateTime)?.getTime() - new Date(a?.dateTime)?.getTime() || b?.orderBy - a?.orderBy
      )
    : [];

  const testPackageNumber: string =
    find(
      find(sections, (section) => lowerCase(section?.title) === CommoditySectionDetail.CommoditySectionTitle)?.fields,
      (field) => lowerCase(field.name) === CommoditySectionDetail.CommoditySectionName
    )?.value ?? '';

  return (
    <Box flexDirection={{ sm: 'column' }} style={{ overflowY: 'scroll' }}>
      <Box m={{ sm: '500' }} mb={{ sm: '600' }}>
        <SummaryContainerStyled>
          <SummaryContentStyled>
            <SummaryItem row={1} iconName="ball-pile" label={t`Material`} value={pipeMaterial ? pipeMaterial : ''} />
            <SummaryItem
              row={2}
              iconName="ruler"
              label={t`Diameter`}
              value={`${pipeDiameter ? `${pipeDiameter}` : '0'}”`}
            />
            <SummaryItem
              row={3}
              iconName="file-alt"
              label={t`Weld Procedures`}
              value={weldedWithProcedure ? weldedWithProcedure.procedure : ''}
              values={map(applicableWeldProcedures, 'procedure')}
            />
            <SummaryItem
              row={4}
              iconName="briefcase"
              label={t`Work Package`}
              value={workPackage?.tag ? workPackage?.tag : ''}
            />
            <SummaryItem row={5} iconName="tasks-alt" label={t`Test Package`} value={testPackageNumber} />
            <SummaryItem row={6} iconName="exclamation-triangle" label={t`Blocks`} value={String(openBlocksCount)} />
          </SummaryContentStyled>
          {isoDocument && (
            <DocumentPreviewContainerStyled>{<IsoContent {...isoDocument} />}</DocumentPreviewContainerStyled>
          )}
        </SummaryContainerStyled>
      </Box>
      {location && (
        <>
          <Separator type={SeparatorType.HORIZONTAL} spacing="000" color="grey.300" />
          <WeldLocation
            weldId={weld.id}
            location={location}
            locationDescription={locationDescription ? locationDescription : ''}
            commodityAssignees={commodityAssignee}
          />
        </>
      )}
      <Separator type={SeparatorType.HORIZONTAL} spacing="000" color="grey.300" />
      {sortedMilestonesActivityHistory && sortedMilestonesActivityHistory?.length > 0 && (
        <Box ml={{ sm: '700' }} mr={{ sm: '500' }} my={{ sm: '700' }} flexDirection={{ sm: 'column' }}>
          <Box mb={{ sm: '600' }}>
            <Heading color="grey.1000" size="600" weight="600">
              <Trans>Activity History</Trans>
            </Heading>
          </Box>
          <Box alignItems={{ sm: 'left' }} flexDirection={{ sm: 'column' }}>
            <Box flexDirection={{ sm: 'column' }} mt={{ sm: '500' }} data-testid="commodity-activity-history">
              {map(sortedMilestonesActivityHistory, (activity, index: number) => (
                <HistoryItem
                  isWeldCommodity={true}
                  key={activity.id}
                  data-testid="weld-activity-history"
                  {...activity}
                  last={sortedMilestonesActivityHistory.length === index + 1}
                  commodityName={weld.type.name}
                  workPackageTag={workPackage?.tag}
                  commodityTag={weld.tag}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
